/**
 * Tab bar + match-results table for the Wholesale Workspace. Two
 * canonicals govern the visuals here:
 *   - AuditTabs (components/audit/AuditTabs.tsx, ~line 46-64) for the
 *     colored tab strip — silver bottom border, marginBottom: md, per
 *     tab sm/md padding + 14px text + 3px navy active underline + the
 *     Excel-matched tab background color when active.
 *   - AuditTable (components/audit/AuditTable.tsx) for the table itself
 *     — auditTable.headerBackground (navy), white caption-type column
 *     titles with white-at-10% column dividers, 12px body cells with
 *     xs/sm padding, zebra rows alternating zebraLight / zebraMain,
 *     silver column borders, monospace for VIN.
 *
 * Wholesale tab → Excel color mapping (matches the existing audit
 * Excel-color tabs verbatim):
 *   cleared           → auditTable.tabColors.completed     (green)
 *   not_cleared       → auditTable.tabColors.discrepancies (red)
 *   unmatched_adesa   → auditTable.tabColors.unclassified  (blue)
 */
import React from "react"
import { Check, Minus, RotateCcw } from "lucide-react"

import { WholesaleTab, WholesaleVehicle, WholesaleVehicleGroups } from "../../api/wholesale"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

interface Props {
  groups: WholesaleVehicleGroups | null
  activeTab: WholesaleTab
  onTabChange: (tab: WholesaleTab) => void
  loading: boolean
  // Draft only: clicking a blue (user_selected) Status cell re-opens the
  // picker. Read-only completed runs pass editable=false / no handler.
  editable?: boolean
  onPickVehicle?: (vehicle: WholesaleVehicle) => void
  // Draft only: restore an excluded vehicle back to the validator.
  onRestoreVehicle?: (vehicle: WholesaleVehicle) => void
}

const TAB_DEFINITIONS: { slug: WholesaleTab; label: string }[] = [
  { slug: "cleared",         label: "Cleared" },
  { slug: "not_cleared",     label: "Not Cleared" },
  { slug: "unmatched_adesa", label: "Unmatched Adesa" },
  { slug: "excluded",        label: "Excluded" },
]

export default function MatchResultsTable({
  groups,
  activeTab,
  onTabChange,
  loading,
  editable = false,
  onPickVehicle,
  onRestoreVehicle,
}: Props) {
  const counts = countsFromGroups(groups)
  const rows = groups ? rowsForTab(groups, activeTab) : []

  return (
    <div style={{ fontFamily: THEME.fonts.primary }}>
      <TabBar activeTab={activeTab} counts={counts} onTabChange={onTabChange} />
      <Table
        rows={rows}
        activeTab={activeTab}
        loading={loading}
        editable={editable}
        onPickVehicle={onPickVehicle}
        onRestoreVehicle={onRestoreVehicle}
      />
    </div>
  )
}

// Status glyph: green check (matched), blue check (user picked), dash
// (unmatched). The blue cell is clickable on a draft to re-pick.
function StatusGlyph({
  vehicle,
  editable,
  onPick,
}: {
  vehicle: WholesaleVehicle
  editable: boolean
  onPick?: (v: WholesaleVehicle) => void
}) {
  if (vehicle.match_state === "user_selected") {
    const clickable = editable && !!onPick && (vehicle.match_candidates ?? []).length > 0
    return (
      <span
        onClick={clickable ? () => onPick!(vehicle) : undefined}
        title={clickable ? "Re-pick Tekion record" : "User-selected"}
        style={{
          color: THEME.colors.semantic.info,
          cursor: clickable ? "pointer" : "default",
          display: "inline-flex",
        }}
      >
        <Check size={15} strokeWidth={3} />
      </span>
    )
  }
  if (vehicle.match_state === "matched") {
    return (
      <span style={{ color: THEME.colors.semantic.success, display: "inline-flex" }} title="Matched">
        <Check size={15} strokeWidth={3} />
      </span>
    )
  }
  if (vehicle.match_state === "excluded") {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary, display: "inline-flex" }} title="Excluded">
        <Minus size={15} strokeWidth={2.5} />
      </span>
    )
  }
  return (
    <span style={{ color: THEME.colors.neutral.light.textSecondary, display: "inline-flex" }} title="No match">
      <Minus size={15} strokeWidth={2.5} />
    </span>
  )
}

// Excluded tab, draft: a restore control returns the vehicle to the validator.
function RestoreControl({
  vehicle,
  onRestore,
}: {
  vehicle: WholesaleVehicle
  onRestore: (v: WholesaleVehicle) => void
}) {
  return (
    <span
      onClick={() => onRestore(vehicle)}
      title="Restore to validator"
      style={{ color: THEME.colors.semantic.info, cursor: "pointer", display: "inline-flex" }}
    >
      <RotateCcw size={15} strokeWidth={2.5} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// Tab strip — mirrors PhotoListTabs
// ---------------------------------------------------------------------------

interface TabBarProps {
  activeTab: WholesaleTab
  counts: Record<WholesaleTab, number>
  onTabChange: (tab: WholesaleTab) => void
}

// Tab → active-tab background color. The first three mirror the audit
// table's Excel-matched tab colors; Excluded uses a neutral gray (set aside).
const TAB_COLOR: Record<WholesaleTab, string> = {
  cleared:         THEME.auditTable.tabColors.completed,
  not_cleared:     THEME.auditTable.tabColors.discrepancies,
  unmatched_adesa: THEME.auditTable.tabColors.unclassified,
  excluded:        THEME.colors.silver,
}

function TabBar({ activeTab, counts, onTabChange }: TabBarProps) {
  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
        marginBottom: THEME.spacing.md,
        overflowX: "auto",
      }}
    >
      {TAB_DEFINITIONS.map((tab) => {
        const active = tab.slug === activeTab
        const tabColor = TAB_COLOR[tab.slug]
        return (
          <div
            key={tab.slug}
            onClick={() => onTabChange(tab.slug)}
            style={{
              padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
              cursor: "pointer",
              backgroundColor: active ? tabColor : "transparent",
              borderBottom: active
                ? `3px solid ${THEME.auditTable.activeTabBorderBottom}`
                : "3px solid transparent",
              color: active ? "#000" : THEME.auditTable.inactiveTabColor,
              fontWeight: active ? 600 : 400,
              fontSize: 14,
              userSelect: "none",
              transition: "background-color 0.15s, border-color 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
            <span
              style={{
                marginLeft: 6,
                color: active ? "#000" : THEME.colors.neutral.light.textSecondary,
                fontWeight: 400,
              }}
            >
              ({counts[tab.slug].toLocaleString()})
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Table — mirrors AuditTable
// ---------------------------------------------------------------------------

interface TableProps {
  rows: WholesaleVehicle[]
  activeTab: WholesaleTab
  loading: boolean
  editable: boolean
  onPickVehicle?: (vehicle: WholesaleVehicle) => void
  onRestoreVehicle?: (vehicle: WholesaleVehicle) => void
}

// Column order mirrors the original Adesa Cleared List file:
// CLIENT, STOCK#, AGE, VIN, YEAR, MAKE, MODEL, COLOR, ODO, STORE, NOTES.
// Cleared / Not Cleared tabs append a 12th TITLE STATUS column from
// Tekion. Unmatched Adesa tab has no Tekion data, so 11 columns only.
const COLUMNS_MATCHED: Column[] = [
  { key: "client",              label: "CLIENT",       width: 120 },
  { key: "adesa_stock",         label: "STOCK#",       width: 100 },
  { key: "age_date",            label: "AGE",          width: 110 },
  { key: "vin",                 label: "VIN",          width: 180, mono: true },
  { key: "year",                label: "YEAR",         width: 70 },
  { key: "make",                label: "MAKE",         width: 110 },
  { key: "model",               label: "MODEL",        width: 130 },
  { key: "color",               label: "COLOR",        width: 100 },
  { key: "odo",                 label: "ODO",          width: 90 },
  { key: "store",               label: "STORE",        width: 220 },
  { key: "notes",               label: "NOTES",        width: 220 },
  { key: "tekion_title_status", label: "TITLE STATUS", width: 140 },
]

const COLUMNS_UNMATCHED: Column[] = [
  { key: "client",      label: "CLIENT", width: 120 },
  { key: "adesa_stock", label: "STOCK#", width: 100 },
  { key: "age_date",    label: "AGE",    width: 110 },
  { key: "vin",         label: "VIN",    width: 180, mono: true },
  { key: "year",        label: "YEAR",   width: 70 },
  { key: "make",        label: "MAKE",   width: 110 },
  { key: "model",       label: "MODEL",  width: 130 },
  { key: "color",       label: "COLOR",  width: 100 },
  { key: "odo",         label: "ODO",    width: 90 },
  { key: "store",       label: "STORE",  width: 220 },
  { key: "notes",       label: "NOTES",  width: 220 },
]

interface Column {
  key: string
  label: string
  width: number
  mono?: boolean
}

function Table({ rows, activeTab, loading, editable, onPickVehicle, onRestoreVehicle }: TableProps) {
  const isMobile = useIsMobile()

  if (loading) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 14,
        }}
      >
        Updating preview…
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 14,
        }}
      >
        No vehicles in this tab.
      </div>
    )
  }

  // On a phone the 12-column table is unusable sideways-scrolled — render
  // each vehicle as a stacked card instead (mirrors the Adesa Run List's
  // AuctionCardList, itself the Image Index card pattern).
  if (isMobile) {
    return (
      <ClearedCardList
        rows={rows}
        activeTab={activeTab}
        editable={editable}
        onRestoreVehicle={onRestoreVehicle}
      />
    )
  }

  // Excluded rows carry no single attributed Tekion record, so (like
  // Unmatched) they show the 11 Adesa columns only.
  const columns =
    activeTab === "unmatched_adesa" || activeTab === "excluded"
      ? COLUMNS_UNMATCHED
      : COLUMNS_MATCHED

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...headerCellStyle(60), textAlign: "center" }}>STATUS</th>
            {columns.map((col) => (
              <th key={col.key} style={headerCellStyle(col.width)}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => {
            const rowBackground =
              rowIdx % 2 === 0
                ? THEME.auditTable.zebraLight
                : THEME.auditTable.zebraMain
            return (
              <tr key={row.adesa_row_order}>
                <td
                  style={{
                    ...dataCellStyle({ key: "status", label: "", width: 60 }, rowBackground),
                    textAlign: "center",
                  }}
                >
                  {activeTab === "excluded" && editable && onRestoreVehicle ? (
                    <RestoreControl vehicle={row} onRestore={onRestoreVehicle} />
                  ) : (
                    <StatusGlyph vehicle={row} editable={editable} onPick={onPickVehicle} />
                  )}
                </td>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={dataCellStyle(col, rowBackground)}
                  >
                    {renderCell(col.key, row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function headerCellStyle(width: number): React.CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: THEME.colors.white,
    textAlign: "left",
    backgroundColor: THEME.auditTable.headerBackground,
    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
    boxSizing: "border-box",
  }
}

function dataCellStyle(col: Column, rowBackground: string): React.CSSProperties {
  return {
    width: col.width,
    minWidth: col.width,
    maxWidth: col.width,
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 12,
    color: THEME.colors.neutral.light.textPrimary,
    backgroundColor: rowBackground,
    borderRight: `1px solid ${THEME.auditTable.borderColor}`,
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: col.mono
      ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
      : "inherit",
  }
}

// Year-like columns render as plain integers (no thousands separator —
// "2019" not "2,019"). Other numeric columns (odometer, mileage) keep
// locale formatting since the comma actually helps readability.
const YEAR_LIKE_FIELDS = new Set(["year", "tekion_year"])

function renderCell(key: string, row: WholesaleVehicle): string {
  const value = (row as unknown as Record<string, unknown>)[key]
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "number") {
    if (YEAR_LIKE_FIELDS.has(key)) return String(Math.trunc(value))
    return value.toLocaleString()
  }
  return String(value)
}

function countsFromGroups(groups: WholesaleVehicleGroups | null): Record<WholesaleTab, number> {
  if (!groups) {
    return { cleared: 0, not_cleared: 0, unmatched_adesa: 0, excluded: 0 }
  }
  return {
    cleared: groups.cleared.length,
    not_cleared: groups.not_cleared.length,
    unmatched_adesa: groups.unmatched_adesa.length,
    excluded: groups.excluded.length,
  }
}

function rowsForTab(groups: WholesaleVehicleGroups, tab: WholesaleTab): WholesaleVehicle[] {
  if (tab === "cleared") return groups.cleared
  if (tab === "not_cleared") return groups.not_cleared
  if (tab === "excluded") return groups.excluded
  return groups.unmatched_adesa
}

// ---------------------------------------------------------------------------
// Mobile card list — read-only stacked cards, one per vehicle. Cite the
// Adesa Run List card pattern (components/wholesale-auction/AuctionRunTable
// AuctionCardList, ~line 406) which itself mirrors Image Index's cards.
// Title Status is shown only on the matched tabs (unmatched rows have no
// Tekion side), colored by tab the way the desktop tab strip is.
// ---------------------------------------------------------------------------

function ClearedCardList({
  rows,
  activeTab,
  editable,
  onRestoreVehicle,
}: {
  rows: WholesaleVehicle[]
  activeTab: WholesaleTab
  editable: boolean
  onRestoreVehicle?: (vehicle: WholesaleVehicle) => void
}) {
  const showTitleStatus = activeTab !== "unmatched_adesa" && activeTab !== "excluded"
  const showRestore = activeTab === "excluded" && editable && !!onRestoreVehicle
  return (
    <div style={cardListStyle}>
      {rows.map((row) => (
        <div key={row.adesa_row_order} style={cardStyle}>
          <div style={cardTitleRowStyle}>
            <span style={cardTitleStyle}>{cardVehicleTitle(row)}</span>
            {showTitleStatus && row.tekion_title_status && (
              <span style={titleStatusChipStyle(activeTab)}>{row.tekion_title_status}</span>
            )}
            {showRestore && (
              <button
                type="button"
                onClick={() => onRestoreVehicle!(row)}
                style={cardRestoreButtonStyle}
              >
                <RotateCcw size={13} strokeWidth={2.5} /> Restore
              </button>
            )}
          </div>
          <div style={cardLineStyle}>
            {[row.adesa_stock ? `Stock ${row.adesa_stock}` : null, row.color]
              .filter(Boolean)
              .join("  ·  ") || "—"}
          </div>
          {row.vin && <div style={cardVinStyle}>VIN {row.vin}</div>}
          <div style={cardSummaryStyle}>
            <CardMetric label="Age" value={row.age_date || "—"} />
            <CardMetric
              label="ODO"
              value={row.odo != null ? row.odo.toLocaleString() : "—"}
            />
            <CardMetric label="Store" value={row.store || "—"} />
          </div>
          {row.notes && <div style={cardNotesStyle}>{row.notes}</div>}
        </div>
      ))}
    </div>
  )
}

function CardMetric({ label, value }: { label: string; value: string }) {
  return (
    <div style={cardMetricStyle}>
      <span style={cardMetricLabelStyle}>{label}</span>
      <span style={cardMetricValueStyle}>{value}</span>
    </div>
  )
}

function cardVehicleTitle(row: WholesaleVehicle): string {
  return [row.year != null ? String(Math.trunc(row.year)) : null, row.make, row.model]
    .filter(Boolean)
    .join(" ") || "Vehicle"
}

const cardListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const cardStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  fontFamily: THEME.fonts.primary,
}

const cardTitleRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: THEME.spacing.sm,
  marginBottom: 2,
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const cardRestoreButtonStyle: React.CSSProperties = {
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  padding: "2px 8px",
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const cardLineStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: 2,
}

const cardVinStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  marginBottom: THEME.spacing.sm,
}

const cardSummaryStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.lg,
  borderTop: `1px solid ${THEME.colors.silver}`,
  paddingTop: THEME.spacing.sm,
}

const cardMetricStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
}

const cardMetricLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const cardMetricValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

const cardNotesStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.sm,
  fontStyle: "italic",
}

// Chip color follows the tab's Excel-matched color: cleared = green,
// not_cleared = red (mirrors the desktop tab strip mapping at the top
// of this file).
function titleStatusChipStyle(tab: WholesaleTab): React.CSSProperties {
  const background =
    tab === "cleared"
      ? THEME.auditTable.tabColors.completed
      : THEME.auditTable.tabColors.discrepancies
  return {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: THEME.borderRadius.sm,
    background,
    color: "#000",
    whiteSpace: "nowrap",
  }
}
